import React, { useEffect, useState } from 'react';
import {
    IOneplaceLibraryContextProp,
    withOneplaceLibraryContext,
    ValidationErrors
} from 'oneplace-components';
import { Api } from '../../data_sources/api';
import { IncidentSubmissionDialog, IncidentSubmissionStatus } from './IncidentSubmissionDialog';
import { IIncidentV2 } from '../../models/Incident';
import { submitIncidentReport } from './utils/submitIncidentReport';

/*
 * Mirrors IncidentV2Footer.tsx (the incident-sign flow), but posts multipart to create a new
 * incident instead of PUTting a signature onto an existing one.
 */

export interface IIncidentReportFooterProps
    extends IOneplaceLibraryContextProp {
    incident: IIncidentV2
    surveyName: string;
    allowAttachIncidentPdf: boolean;
    errorsShown: boolean;
    errors: string[];
    onErrorsDismissed: () => void;
    onCloseSubmitDialog: () => void;
    isSubmitDialogOpen: boolean;
    region: string;
    path: string;
    onSubmitted: (incidentNo: number) => void;
}

const IncidentReportFooterFn: React.FC<IIncidentReportFooterProps> = (props) => {

    const [submitDialogContent, setSubmitDialogContent] = useState<string | React.ReactElement<any>>('')
    const [submissionStatus, setSubmissionStatus] = useState<IncidentSubmissionStatus>(null)
    const submittedIncidentNo = React.useRef<number>(0)

    useEffect(() => {
        if (props.isSubmitDialogOpen && !submissionStatus && submitDialogContent === '') {
            setSubmitDialogContent(`Confirm submitting ${props.ctx.i18next.t('customLabel_incident')}?`)
        }
    }, [props.isSubmitDialogOpen, submissionStatus, submitDialogContent, props.ctx.i18next])

    function onCloseSubmitDialog() {
        if (submissionStatus === 'submitting') {
            return;
        }

        props.onCloseSubmitDialog()
        // only notify the parent once the user has dismissed the success dialog, otherwise the
        // page swaps to the "thanks for submitting" screen while the dialog is still on top of it
        if (submissionStatus === 'success') {
            props.onSubmitted(submittedIncidentNo.current)
        }
        setSubmitDialogContent('')
        setSubmissionStatus(null)
    }

    async function onConfirmSubmitDialog(emails: string|undefined, includePdf: boolean) {
        if (!submissionStatus) {
            setSubmissionStatus('submitting')
            await doSubmit(emails, includePdf)
        } else {
            onCloseSubmitDialog()
        }
    }

    const doSubmit = async (emails: string|undefined, includePdf: boolean) => {
        try {
            const api = new Api(props.region, props.path)
            // the confirmation email and PDF choice ride along on this same request - the backend
            // queues the email async, so there is no second call to handle here
            const response = await submitIncidentReport(api, props.incident, props.ctx.imageStorage, emails, includePdf)
            submittedIncidentNo.current = response.incidentNo
            setSubmitDialogContent(`${props.ctx.i18next.t('customLabel_incident')} submitted successfully!`)
            setSubmissionStatus('success')
        } catch (e) {
            setSubmissionStatus('error')
            setSubmitDialogContent(`${e}`)
        }
    }

    return (
        <>
            <ValidationErrors
                errorsShown={props.errorsShown}
                errors={props.errors}
                onErrorsDismissed={props.onErrorsDismissed}
            />
            <IncidentSubmissionDialog
                isOpen={props.isSubmitDialogOpen}
                status={submissionStatus}
                content={submitDialogContent}
                siteName={props.incident.location || props.incident.site?.name || ''}
                surveyName={props.surveyName}
                allowAttachIncidentPdf={props.allowAttachIncidentPdf}
                onClose={() => onCloseSubmitDialog()}
                onConfirm={(email: string|undefined, includePdf: boolean) => onConfirmSubmitDialog(email, includePdf)}
            />
        </>
    )
};

export const IncidentReportFooter = withOneplaceLibraryContext(IncidentReportFooterFn);
